import { inject, Injectable } from '@angular/core';
import {
	BaseActivityService,
	UpdateOptions,
} from '@context/frontend/api-client';
import { collectionCount } from '@context/frontend/common';
import { ContentService } from '@context/frontend/content';
import { MediaService } from '@context/frontend/media';
import { NotificationService } from '@context/frontend/notification';
import { Fetchable, PaginatedObject } from '@context/frontend/pagination';
import { ActivityType } from '@context/shared/types/activity';
import {
	Content,
	createThumbnail,
	PresetCompressions,
	Thumbnail,
} from '@context/shared/types/common';
import { ContentLog, createLog } from '@context/shared/types/log';
import {
	ContentMedia,
	isMediaImage,
	UploadedAsset,
} from '@context/shared/types/media';
import { getName, User } from '@context/shared/types/user';
import { sanitize } from '@context/shared/utils';
import {
	collection,
	collectionGroup,
	CollectionReference,
	doc,
	DocumentData,
	DocumentReference,
	DocumentSnapshot,
	getDoc,
	getDocs,
	limit,
	query,
	Query,
	setDoc,
	startAfter,
	Timestamp,
	UpdateData,
	updateDoc,
} from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class LogService
	extends BaseActivityService<ContentLog>
	implements Fetchable<ContentLog>
{
	private static readonly CollectionId = 'logs';

	readonly mediaService = inject(MediaService);
	readonly contentService = inject(ContentService);
	readonly notificationService = inject(NotificationService);

	/**
	 * Creates a single log under the provided parent content.
	 *
	 * @param log.files raw files to upload for this log. When omitted, any
	 * pre-uploaded `assets` are used instead.
	 * @param log.assets already-uploaded assets to reference without re-uploading.
	 * Used by {@link createForCrops} so the same upload can be shared across
	 * multiple crops without duplicating the bytes.
	 */
	async create(
		parentRef: DocumentReference<Content>,
		log: Partial<ContentLog> & {
			files?: File[];
			assets?: UploadedAsset[];
		},
	) {
		const organizationId = this.organizationId.value;
		if (!organizationId) throw new Error('Organization ID is required');

		// Copy the payload so we never mutate the caller's object. The same
		// payload is reused when creating a log across multiple crops, so
		// mutating it here previously caused media to only attach to the first
		// crop.
		const { files, assets: providedAssets, ...rest } = log;

		const now = Timestamp.now();
		const payload = createLog(rest) as ContentLog;

		payload.createdAt = now;
		payload.updatedAt = now;
		payload.deletedAt = null;

		payload.id = crypto.randomUUID();
		payload.organizationId = organizationId;

		const collectionLog = collection(parentRef, LogService.CollectionId);
		const logRef = doc(
			collectionLog,
			payload.id,
		) as DocumentReference<ContentLog>;

		const parent = (await getDoc(parentRef)).data() as Content;

		await setDoc(logRef, sanitize(payload)).then(() => {
			// if the parent content is not owned by the log creator, send notification
			if (parent.createdBy.id !== this.user.value?.id) {
				this.sendNotification('user-added-log', parent, {
					name: payload.name,
					id: payload.id,
				});
			}
		});

		// Reuse pre-uploaded assets when provided (multi-crop), otherwise upload
		// the raw files a single time for this log.
		const assets =
			providedAssets ??
			(files?.length ? await this.uploadAssets(files) : undefined);

		if (assets?.length) {
			const { thumbnails, media, firstImageMedia } =
				await this.createLogMedia(
					assets,
					parentRef,
					logRef,
					payload.thumbnails,
				);

			if (firstImageMedia) {
				await this.contentService.setContentMediaDerivedThumbnail({
					derived: 'log',
					content: parent,
					ref: firstImageMedia.ref,
					media: firstImageMedia.data,
				});
			}

			await this.update(
				parentRef,
				{ thumbnails, media: media.map((m) => m.ref) },
				logRef,
				{ addActivityLog: false },
			);
		}

		if (payload.createdBy || this.user.value?.id) {
			await this.logActivity({
				entity: logRef as DocumentReference<ContentLog>,
				createdBy:
					(payload.createdBy as DocumentReference<User>) ??
					this.user.value?.id,
				activity: { type: 'create' },
			});
		}

		return payload;
	}

	/**
	 * Creates the same log across multiple crops.
	 *
	 * Any attached files are uploaded to storage exactly once and the resulting
	 * assets are shared across every crop's log. Each crop still gets its own
	 * lightweight media document pointing at the shared storage path, so the
	 * per-content subcollection model is preserved without duplicating the
	 * (potentially huge) uploads.
	 */
	async createForCrops(
		cropRefs: DocumentReference<Content>[],
		log: Partial<ContentLog> & { files?: File[] },
	) {
		const { files, ...rest } = log;

		const assets = files?.length
			? await this.uploadAssets(files)
			: undefined;

		const results: { parentContentId: string; log: ContentLog }[] = [];
		for (const parentRef of cropRefs) {
			const created = await this.create(parentRef, { ...rest, assets });
			results.push({ parentContentId: parentRef.id, log: created });
		}

		return results;
	}

	/**
	 * Uploads each file's bytes to storage a single time, returning reusable
	 * asset pointers. Failed uploads are logged and skipped so one bad file
	 * does not prevent the rest from being attached.
	 */
	private async uploadAssets(files: File[]): Promise<UploadedAsset[]> {
		const results = await Promise.all(
			files.map((file) =>
				this.mediaService.uploadAsset(file).catch((error) => {
					console.error(
						'There was an issue uploading the media',
						error,
					);
					return null;
				}),
			),
		);

		return results.filter((asset): asset is UploadedAsset => !!asset);
	}

	/**
	 * The logRef is required here so we can easily update the reference without needing to make
	 * a large id query against all of the collectionGroups.
	 *
	 * @param payload.files The new files that will be uploaded onto the log
	 */
	async update(
		parentRef: DocumentReference<Content>,
		payload: Partial<
			ContentLog & {
				files?: File[];
				removedMedia?: DocumentReference<ContentMedia>[];
			}
		>,
		logRef: DocumentReference<ContentLog>,
		options: UpdateOptions,
	) {
		const removedMedia = payload.removedMedia;
		delete payload.removedMedia;

		// detaches the log from the media item so the media item can be managed independently now
		if (removedMedia?.length) {
			payload.thumbnails = payload.thumbnails
				? [
						...payload.thumbnails.filter(
							(t) =>
								!removedMedia.some((m) => m.id === t.ref?.id),
						),
					]
				: [];

			await Promise.all(
				removedMedia.map((m) =>
					this.mediaService.update({ log: null }, m, {
						addActivityLog: true,
						type: 'update',
					}),
				),
			);
		}

		const files = payload.files;
		delete payload.files;

		if (files?.length) {
			const assets = await this.uploadAssets(files);
			const { thumbnails, media } = await this.createLogMedia(
				assets,
				parentRef,
				logRef,
				payload.thumbnails,
			);
			payload.thumbnails = thumbnails;
			// Append new media to existing media instead of replacing
			payload.media = [
				...(payload.media || []),
				...media.map((m) => m.ref),
			];
		}

		payload.updatedAt = Timestamp.now();
		const value = sanitize(payload);
		await updateDoc(logRef, value as UpdateData<ContentLog>);

		if (this.user.value?.id) {
			await this.logActivity({
				entity: logRef,
				activity: { type: options.type as ActivityType },
				createdBy: this.user.value?.id,
			});
		}

		// Fetch the complete updated log to ensure we return all fields
		const updatedLog = (await getDoc(logRef)).data() as ContentLog;
		return updatedLog || value;
	}

	/**
	 * The logRef is required here so we ca easily "delete" the reference without needing to
	 * make a large id query against all of the collectionGroups
	 */
	async delete(
		parent: Content,
		logRef: DocumentReference<ContentLog>,
		log?: ContentLog,
	) {
		if (!log) log = (await getDoc(logRef)).data() as ContentLog;

		const now = Timestamp.now();
		const batch: Promise<unknown>[] = [
			updateDoc(
				logRef,
				sanitize({
					updatedAt: now,
					deletedAt: now,
				}) as UpdateData<ContentLog>,
			),
		];

		if (this.user.value?.id) {
			batch.push(
				this.logActivity({
					entity: logRef,
					activity: { type: 'delete' },
					createdBy: this.user.value?.id,
				}),
			);
		}

		if (log.media.length) {
			batch.push(
				...log.media.map((mediaRef) =>
					this.mediaService.delete(
						parent,
						mediaRef as DocumentReference<ContentMedia>,
					),
				),
			);
		}

		// empty object to ensure deletion success
		return Promise.all(batch).then(() => ({}));
	}

	async archive(
		parentRef: DocumentReference<Content>,
		logRef: DocumentReference<ContentLog>,
		log?: ContentLog,
	) {
		if (!log) log = (await getDoc(logRef)).data() as ContentLog;

		const parent = (await getDoc(parentRef)).data() as Content;

		const now = Timestamp.now();
		const batch: Promise<unknown>[] = [
			this.update(parentRef, { archivedAt: now }, logRef, {
				addActivityLog: true,
				type: 'archive',
			}).then(() => {
				if (parent.createdBy.id !== this.user.value?.id) {
					this.sendNotification('user-archived-log', parent, log);
				}
			}),
		];

		batch.push(
			...log.media.map((mediaRef) =>
				this.mediaService.archive(
					parent,
					mediaRef as DocumentReference<ContentMedia>,
				),
			),
		);

		return Promise.all(batch).then(([payload]) => payload);
	}

	async unarchive(
		parentRef: DocumentReference<Content>,
		logRef: DocumentReference<ContentLog>,
		log?: ContentLog,
	) {
		if (!log) log = (await getDoc(logRef)).data() as ContentLog;

		const batch: Promise<unknown>[] = [
			this.update(parentRef, { archivedAt: null }, logRef, {
				addActivityLog: true,
				type: 'unarchive',
			}),
		];

		batch.push(
			...log.media.map((mediaRef) =>
				this.mediaService.unarchive(
					mediaRef as DocumentReference<ContentMedia>,
				),
			),
		);

		return Promise.all(batch).then(([payload]) => payload);
	}

	async fetch(
		paginated?: PaginatedObject<ContentLog>,
	): Promise<PaginatedObject<ContentLog>> {
		if (!paginated) paginated = new PaginatedObject<ContentLog>();

		let logQuery: Query<ContentLog> | null = null;
		if (paginated.parentRef) {
			logQuery = query(
				collection(
					paginated.parentRef as DocumentReference<Content>,
					LogService.CollectionId,
				) as CollectionReference<ContentLog, DocumentData>,
			);
		} else if (this.organizationId.value) {
			logQuery = query(
				collectionGroup(
					this.firestore,
					LogService.CollectionId,
				) as CollectionReference<ContentLog>,
			);
		} else {
			throw new Error('Collection could not be determined');
		}

		if (!paginated.totalElements) {
			paginated.totalElements = await firstValueFrom(
				collectionCount(query(logQuery, ...paginated.constraints)),
			);
		}

		// if the object already contains the data, don't fetch again
		if (paginated.page) return paginated;

		const constraints = [...paginated.constraints];
		const lastDoc = paginated.previousPage?.last ?? null;
		if (lastDoc) constraints.push(startAfter(lastDoc));
		if (paginated.pageSize) constraints.push(limit(paginated.pageSize));
		const snapshot = await getDocs(query(logQuery, ...constraints));

		let last: DocumentSnapshot | null = null;
		const items = snapshot.docs.map((d) => {
			last = d;
			return { data: d.data(), ref: d.ref, id: d.id };
		});

		paginated.pages = Object.assign({}, paginated.pages, {
			[paginated.currentPage as number]: { last, items },
		});

		return paginated;
	}

	sendNotification(
		type: 'user-added-log' | 'user-archived-log',
		parent: Content,
		log: Pick<ContentLog, 'id' | 'name'>,
	) {
		const user = this.user.value as User;
		this.notificationService.send(
			{
				createdById: user.id,
				type,
				args: {
					id: log.id,
					name: log.name,
					user: getName(user),
					content: parent.name,
					contentId: parent.id,
				},
			},
			parent.createdBy.id,
		);
	}

	/**
	 * Creates the media documents for a log from already-uploaded assets and
	 * generates the thumbnails based on the media created.
	 *
	 * The assets have already been uploaded to storage (see
	 * {@link uploadAssets}); this only writes the lightweight media documents,
	 * so the same asset can be referenced by multiple crops without duplicating
	 * the underlying bytes.
	 */
	private async createLogMedia(
		assets: UploadedAsset[],
		parentRef: DocumentReference<Content>,
		logRef: DocumentReference<ContentLog>,
		thumbnails: Thumbnail[] = [],
	) {
		if (!assets?.length) return { thumbnails: [], media: [] };

		const batch: Promise<{
			data: ContentMedia;
			ref: DocumentReference<ContentMedia>;
		} | null>[] = [];

		const errors = new Map<number, UploadedAsset>();
		assets.forEach((asset, index) =>
			batch.push(
				this.mediaService
					.createRecord(parentRef, asset, { log: logRef })
					.catch((error) => {
						errors.set(index, asset);
						console.error(
							'There was an issue creating the media record',
							error,
						);
						return null;
					}),
			),
		);

		if (errors.size)
			console.error(
				'There was an issue creating all of the media records',
				errors,
			);

		const { media } = await Promise.all(batch)
			.catch((error) => {
				console.error(error);
				throw new Error('There was an issue with the requests');
			})
			.then((media) => ({
				media: media.filter((m) => !!m), // removes failed records
				errors,
			}));

		// todo: make this a choice by the user eventually, but defaulting to first that matches
		const firstImageMedia = media.find((m) => isMediaImage(m.data.type));
		if (firstImageMedia) {
			// remove any other previous log derived thumbnail since there should only be one
			thumbnails = [...thumbnails.filter((t) => t.derived !== 'media')];
			thumbnails.push(
				createThumbnail({
					storagePath: firstImageMedia.data.storagePath,
					maxResolution: PresetCompressions.max.maxWidthOrHeight,
					derived: 'media',
					ref: firstImageMedia.ref,
				}),
				createThumbnail({
					storagePath: firstImageMedia.data.thumbnailPath as string,
					maxResolution:
						PresetCompressions.thumbnail.maxWidthOrHeight,
					derived: 'media',
					ref: firstImageMedia.ref,
				}),
			);
		}

		return { thumbnails, media, firstImageMedia };
	}
}
